import { useState } from 'react'
import { User } from '../types'
import { updateUserRole, addUser } from '../lib/api'
import UserAutocomplete from './UserAutocomplete'
import './ExecutorRoleManagement.css'

interface ExecutorRoleManagementProps {
  users: User[]
  loading?: boolean
  onRefresh?: () => void
}

function ExecutorRoleManagement({ users, loading, onRefresh }: ExecutorRoleManagementProps) {
  const [updating, setUpdating] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  })
  const [adding, setAdding] = useState(false)
  
  const handleUserSelect = (user: User | null) => {
    setSelectedUser(user)
    if (user) {
      // При выборе пользователя заполняем имя и email автоматически
      setFormData({
        name: user.name,
        email: user.email,
      })
    } else {
      // Если очистили выбор, очищаем только имя, email оставляем для ручного ввода
      setFormData({
        name: '',
        email: formData.email,
      })
    }
  }

  const handleAddExecutor = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.name || !formData.email) {
      alert('Prosím vyplňte všechna pole')
      return
    }

    try {
      setAdding(true)
      // Создаем нового пользователя с ролью executor или обновляем существующего
      const result = await addUser(formData.email, formData.name, 'executor')
      setFormData({ name: '', email: '' })
      setSelectedUser(null)
      setShowAddForm(false)
      if (onRefresh) {
        onRefresh()
      }
      // Показываем соответствующее сообщение в зависимости от того, был ли пользователь создан или обновлен
      if (result.updated) {
        alert('Uživatel již existoval a jeho role byla aktualizována na řešitele')
      } else {
        alert('Uživatel byl přidán jako řešitel')
      }
    } catch (error: any) {
      console.error('Chyba při přidávání uživatele:', error)
      // Если ошибка связана с правами доступа, показываем более понятное сообщение
      if (error.message && (error.message.includes('nemůže') || error.message.includes('cannot') || error.message.includes('přiřazovat'))) {
        alert('Nemáte oprávnění upravit tohoto uživatele. ' + (error.message.includes('přiřazovat') ? 'Správce budov může přiřazovat roli řešitele pouze uživatelům bez role nebo s rolí žadatele.' : 'Tento uživatel má roli, kterou může změnit pouze IT administrátor.'))
      } else {
        alert(error.message || 'Chyba při přidávání uživatele')
      }
    } finally {
      setAdding(false)
    }
  }

  const handleToggleExecutorRole = async (userId: string, currentRole: string | null) => {
    if (updating) return

    const isExecutor = currentRole === 'executor'
    const newRole = isExecutor ? 'requester' : 'executor'
    
    const actionText = isExecutor ? 'odebrat' : 'přiřadit'
    const currentRoleText = currentRole === 'executor' ? 'řešitele' : 
                            currentRole === 'requester' ? 'žadatele' :
                            currentRole === 'director' ? 'ředitele' :
                            currentRole === 'financist' ? 'hlavního ekonoma' :
                            currentRole === 'referent' ? 'obchodního referenta' :
                            currentRole === 'referent_eko' ? 'referenta EKO' :
                            currentRole === 'vedouci' ? 'vedoucího' :
                            currentRole === 'property_referent' ? 'referenta správy majetku' :
                            currentRole === null ? 'uživatele bez role' :
                            'uživatele'
    
    if (!confirm(`Opravdu chcete ${actionText} roli řešitele tomuto uživateli (aktuální role: ${currentRoleText})?`)) {
      return
    }

    try {
      setUpdating(userId)
      await updateUserRole(userId, newRole)
      if (onRefresh) {
        onRefresh()
      }
      alert(`Role byla úspěšně ${isExecutor ? 'odebrána' : 'přiřazena'}`)
    } catch (error: any) {
      console.error('Chyba při změně role:', error)
      alert(error.message || 'Chyba při změně role')
    } finally {
      setUpdating(null)
    }
  }

  // Фільтруем пользователей: показываем ТОЛЬКО обычных решателей (executor)
  // vedouci_executor, správce budov, správce IT НЕ должны быть в списке
  // Správce budov может управлять только обычными исполнителями
  const availableUsers = users.filter(u => 
    u.role === 'executor'
  )

  if (loading) {
    return <div className="executor-role-management">Načítání...</div>
  }

  return (
    <div className="executor-role-management">
      <div className="management-header">
        <h2>Správa rolí řešitelů</h2>
        <p className="subtitle">Můžete přiřazovat a odebírat roli řešitele uživatelům, nebo přidat nového uživatele jako řešitele</p>
        <button 
          className="add-button"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? '✕ Zrušit' : '+ Přidat nového řešitele'}
        </button>
      </div>

      {showAddForm && (
        <div className="add-user-form">
          <form onSubmit={handleAddExecutor}>
            <div className="form-group">
              <label htmlFor="email-input">Email *</label>
              <UserAutocomplete
                value={selectedUser}
                onChange={handleUserSelect}
                placeholder="Začněte psát email nebo jméno..."
                required={false}
                disabled={adding}
              />
              <small style={{ display: 'block', marginTop: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                Při výběru se jméno vyplní automaticky
              </small>
            </div>
            
            <div className="form-group">
              <label htmlFor="name">Jméno *</label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Jméno a příjmení"
                required
                disabled={adding}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem'
                }}
              />
            </div>
            <div className="form-actions">
              <button type="submit" disabled={adding} className="submit-button">
                {adding ? 'Přidávání...' : 'Přidat jako řešitele'}
              </button>
              <button 
                type="button" 
                onClick={() => {
                  setShowAddForm(false)
                  setFormData({ name: '', email: '' })
                  setSelectedUser(null)
                }}
                disabled={adding}
                className="cancel-button"
              >
                Zrušit
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-section">
        <h3>Všichni uživatelé ({availableUsers.length})</h3>
        {availableUsers.length === 0 ? (
          <p className="empty-message">Žádní uživatelé</p>
        ) : (
          <div className="executors-table-container">
            <table className="executors-table">
              <thead>
                <tr>
                  <th>Jméno</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Akce</th>
                </tr>
              </thead>
              <tbody>
                {availableUsers.map(user => {
                  const isExecutor = user.role === 'executor'
                  const getRoleLabel = (role: string | null, adminType?: string | null) => {
                    if (role === 'executor') return 'Řešitel'
                    if (role === 'requester') return 'Žadatel'
                    if (role === 'admin' && adminType === 'spravce_budov') return 'Správce budov'
                    if (role === 'admin' && adminType === 'spravce_it') return 'Správce IT'
                    if (role === 'admin') return 'Administrátor'
                    return role || 'Bez role'
                  }
                  
                  const getRoleClass = (role: string | null) => {
                    if (role === 'executor') return 'role-executor'
                    if (role === 'requester') return 'role-requester'
                    if (role === 'admin') return 'role-admin'
                    return 'role-none'
                  }
                  
                  return (
                    <tr key={user.id} className={getRoleClass(user.role)}>
                      <td><strong>{user.name}</strong></td>
                      <td>{user.email}</td>
                      <td>{getRoleLabel(user.role, user.admin_type)}</td>
                      <td>
                        <button
                          className={isExecutor ? "remove-button" : "add-button"}
                          onClick={() => handleToggleExecutorRole(user.id, user.role)}
                          disabled={updating === user.id}
                          title={isExecutor ? "Odebrat roli řešitele" : "Přiřadit roli řešitele"}
                        >
                          {updating === user.id ? '⏳' : (isExecutor ? '✕ Odebrat' : '+ Přiřadit')}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default ExecutorRoleManagement

